import { SearchForMedicalPaOrdersFromPostgresParams } from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { FilterValues } from "../../../../data-model/filterValues";
import { MedicalOrderReviewStatus } from "../../../../enums/orderReviewStatus";
import { MasterAuthStatusValue } from "../../../../enums/worklistEnums";
import {
  processFilterData,
  transformDateFields,
  transformObjectToFacetFiltersForPostgres,
} from "../../../../utils/queryConstraint";
import { swapUnderscoreHyphen } from "../../../../utils/swapUnderscoreWithHypen";

/**
 * Builds a Postgres-compatible search payload for Review Pending Orders, scoped to
 * the current user with the review-pending review status and the given auth status segment.
 * When a sort field is provided, it is included as `sort_by` / `sort_dir` so the API
 * returns rows in the requested order.
 *
 * @param validFilters - Currently applied filter values
 * @param searchTerm - Free-text search string
 * @param orgId - Organization ID for the query
 * @param userId - Current user's ID (used for the assigned_to filter)
 * @param authStatus - Optional auth status segment (defaults to "no_auth_required")
 * @param sortByField - Optional canonical API field name to sort by
 * @param sortAscending - Optional sort direction; defaults to ascending when sortByField is set
 * @returns Postgres search payload with facet filters, date filters, sort params, and pagination defaults
 */
export const generateFilterPayloadForReviewPendingOrders = (
  validFilters: FilterValues[],
  searchTerm: string,
  orgId: string,
  userId: string,
  authStatus?: string,
  sortByField?: string,
  sortAscending?: boolean,
): SearchForMedicalPaOrdersFromPostgresParams => {
  const filterData = processFilterData(validFilters);
  const masterAuthStatusValue = authStatus
    ? swapUnderscoreHyphen(authStatus)
    : MasterAuthStatusValue.NoAuthRequired;
  const preComputedFilterData = {
    ...filterData,
    medical_order_review_status: [
      MedicalOrderReviewStatus.AGENT_ORDER_REVIEW_PENDING,
    ],
    master_auth_status: [masterAuthStatusValue],
    assigned_to: [userId],
  };

  const createdFilterQuery = transformObjectToFacetFiltersForPostgres(
    preComputedFilterData,
  );

  const dateFilterQuery = transformDateFields(preComputedFilterData);
  const payload: SearchForMedicalPaOrdersFromPostgresParams = {
    org_id: orgId || "",
    query: searchTerm ?? "",
    facet_filters: createdFilterQuery,
    hits_per_page: 100,
    page: 0,
    timezone_offset: new Date().getTimezoneOffset(),
    ...dateFilterQuery,
    ...(sortByField
      ? {
          sort_by: sortByField,
          sort_dir: sortAscending === false ? "desc" : "asc",
        }
      : {}),
  };
  return payload;
};
